import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styles from './HypothesisDetail.module.css';

export const HypothesisDetail: React.FC = () => {
  const { schemaId, filename } = useParams<{ schemaId: string; filename: string }>();
  const navigate = useNavigate();

  const [hypothesis, setHypothesis] = useState<any>(null);
  const [linkedTests, setLinkedTests] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const loadLinkedData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/hypotheses/${schemaId}/${filename}/linked`);
      if (response.ok) {
        const result = await response.json();
        setHypothesis(result.hypothesis);
        setLinkedTests(result.tests || []);
      } else {
        setHypothesis(null);
      }
    } catch (error) {
      console.error('Error loading hypothesis:', error);
      setHypothesis(null);
    } finally {
      setLoading(false);
    }
  }, [schemaId, filename]);

  useEffect(() => {
    if (schemaId && filename) {
      loadLinkedData();
    }
  }, [schemaId, filename, loadLinkedData]);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <h3>Loading...</h3>
          <p>Please wait while we load the hypothesis details</p>
        </div>
      </div>
    );
  }

  if (!hypothesis) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <h3>Hypothesis not found</h3>
          <p>The requested hypothesis could not be found or the file no longer exists</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate(-1)}>
          ← Back
        </button>
        <div className={styles.titleRow}>
          <div className={styles.titleSection}>
            <h1 className={styles.title}>{hypothesis.title}</h1>
            <div className={styles.filename}>{hypothesis.filename}</div>
            <div className={styles.badgeRow}>
              <span className={styles.categoryBadge}>{hypothesis.category || hypothesis.strategy}</span>
              <span className={styles.severityBadge} data-severity={hypothesis.severity?.toLowerCase()}>
                {hypothesis.severity}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.contentArea}>
        {hypothesis.description && (
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Description</h3>
            <p className={styles.descriptionText}>{hypothesis.description}</p>
          </div>
        )}

        {hypothesis.testSteps && hypothesis.testSteps.length > 0 && (
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Exploitation Chain</h3>
            <ol className={styles.stepsList}>
              {hypothesis.testSteps.map((step: string, idx: number) => (
                <li key={idx}>{step}</li>
              ))}
            </ol>
          </div>
        )}

        {hypothesis.expectedResult && (
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Success Criteria</h3>
            <p className={styles.descriptionText}>{hypothesis.expectedResult}</p>
          </div>
        )}

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Related Tests {linkedTests.length > 0 && `(${linkedTests.length})`}</h3>
          {linkedTests.length === 0 ? (
            <div className={styles.emptyState}>
              <h3>No tests found</h3>
              <p>No test files have been generated from this hypothesis yet</p>
            </div>
          ) : (
            <div className={styles.testsList}>
              {linkedTests.map((test: any) => (
                <div
                  key={test.filename}
                  className={styles.testCard}
                  onClick={() => navigate(`/tests/${schemaId}/${test.filename}`)}
                >
                  <div className={styles.testCardTitle}>{test.filename}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
